import { EventBus, type Unsubscribe } from '../events/event_bus';
import { QosController } from '../qos/qos_controller';
import { LiveSession, PlaybackSession } from '../session/session';
import { ClipSession } from '../session/clip_session';
import { SignalingClient } from '../signaling/signaling_client';
import type { ListEventsMsg, EventListMsg, EventListEvent } from '../protocol/signaling';
import type {
  AuthContext,
  ClipSessionRequest,
  ListRecordingsRequest,
  LiveSessionRequest,
  PlaybackSessionRequest,
  RecordingEvent,
  SdkEvent,
  SessionSnapshot,
  SignalingConnectionState,
  VgpsClipSession,
  VgpsLiveSession,
  VgpsLivekitWebConfig,
  VgpsLivekitWebRuntime,
  VgpsPlaybackSession,
} from './types';

let sessionCounter = 0;
function nextSessionId(prefix: string): string {
  sessionCounter += 1;
  return `${prefix}-${Date.now()}-${sessionCounter}`;
}

class RuntimeImpl implements VgpsLivekitWebRuntime {
  private readonly events = new EventBus<SdkEvent>();
  private readonly signalingClient: SignalingClient;
  private readonly qosController = new QosController();
  private readonly sessions = new Map<string, VgpsLiveSession | VgpsPlaybackSession>();
  private authContext: AuthContext | null = null;

  constructor(private readonly config: VgpsLivekitWebConfig) {
    this.signalingClient = new SignalingClient(config.signalingServer);

    // Forward signaling connection state changes to the SDK event bus
    this.signalingClient.onStateChange((state) => {
      this.events.emit({ type: 'signaling_connection', state });
    });
  }

  async setAuthContext(auth: AuthContext): Promise<void> {
    this.authContext = auth;
  }

  async connectSignaling(): Promise<void> {
    if (!this.authContext) {
      throw new Error('Call setAuthContext() before connectSignaling().');
    }
    await this.signalingClient.connect(this.authContext);
  }

  async disconnectSignaling(): Promise<void> {
    await this.signalingClient.disconnect();
  }

  async openLiveSession(request: LiveSessionRequest): Promise<VgpsLiveSession> {
    this.assertConnected();
    const session = new LiveSession({
      sessionId: nextSessionId('live'),
      deviceSerial: request.deviceSerial,
      channelId: request.channelId,
      qualityHint: request.playOptions?.qualityHint,
      mode: 'live',
      globalEvents: this.events,
      signaling: this.signalingClient,
      onClose: this.handleSessionClosed,
      streamSocketFactory: this.config.streamSocketTransportFactory,
      relayHostOverride: this.config.relayHostOverride,
    });
    this.sessions.set(session.sessionId, session);
    return session;
  }

  async openPlaybackSession(request: PlaybackSessionRequest): Promise<VgpsPlaybackSession> {
    this.assertConnected();
    const session = new PlaybackSession(
      {
        sessionId: nextSessionId('playback'),
        deviceSerial: request.deviceSerial,
        channelId: request.channelId,
        qualityHint: request.playOptions?.qualityHint,
        mode: 'playback',
        startTsMs: request.timeRange.startTsMs,
        globalEvents: this.events,
        signaling: this.signalingClient,
        onClose: this.handleSessionClosed,
        streamSocketFactory: this.config.streamSocketTransportFactory,
        relayHostOverride: this.config.relayHostOverride,
      },
      request.timeRange.startTsMs,
    );
    this.sessions.set(session.sessionId, session);
    return session;
  }

  subscribe(listener: (event: SdkEvent) => void): Unsubscribe {
    return this.events.subscribe(listener);
  }

  signalingConnectionState(): SignalingConnectionState {
    return this.signalingClient.state;
  }

  listSessions(): SessionSnapshot[] {
    return Array.from(this.sessions.values()).map((s) => ({
      sessionId: s.sessionId,
      deviceSerial: s.deviceSerial,
      channelId: s.channelId,
      state: s.state,
      transportFamily: s.transportFamily,
      codec: s.codec,
    }));
  }

  async openClipSession(req: ClipSessionRequest): Promise<VgpsClipSession> {
    if (!req.deviceSerial) throw new Error('deviceSerial is required');
    if (!req.channelId) throw new Error('channelId is required');
    const sessionId = nextSessionId('clip');
    const clip = new ClipSession(sessionId, req, {
      signalingUrl: this.config.signalingServer.url,
      iceServers: req.iceServers ?? this.config.transportDefaults?.iceServers ?? [],
    });
    await clip.start();
    return clip;
  }

  async listRecordings(req: ListRecordingsRequest): Promise<RecordingEvent[]> {
    this.assertConnected();
    if (!req.deviceSerial) throw new Error('deviceSerial is required');
    const sinceMs = req.sinceMs ?? Date.now() - 7 * 86_400_000;
    const untilMs = req.untilMs ?? Date.now();
    const order = req.order ?? 'desc';
    const maxCount = req.maxCount ?? 100;
    const timeoutMs = req.timeoutMs ?? 5_000;
    const requestId = `lst-${Math.random().toString(36).slice(2, 10)}`;

    const reply = await this.signalingClient.request<ListEventsMsg, EventListMsg>(
      {
        type: 'list_events',
        request_id: requestId,
        device_serial: req.deviceSerial,
        since_us: sinceMs * 1000,
        until_us: untilMs * 1000,
        max_count: maxCount,
        order,
      },
      { responseType: 'event_list', timeoutMs },
    );
    if (reply.status !== 'ok') {
      throw new Error(reply.message ?? 'event index unavailable on device');
    }
    return (reply.events ?? []).map(normalizeEvent);
  }

  async dispose(): Promise<void> {
    for (const session of Array.from(this.sessions.values())) {
      await session.close();
    }
    this.sessions.clear();
    await this.signalingClient.disconnect();
    this.events.clear();
    activeRuntimeCount--;
  }

  private readonly handleSessionClosed = (sessionId: string): void => {
    this.sessions.delete(sessionId);
  };

  private assertConnected(): void {
    if (this.signalingClient.state !== 'connected') {
      throw new Error('connectSignaling() must complete before opening sessions.');
    }
  }
}

// ── Single-instance guard ────────────────────────────────────────────────────
// Each runtime owns a SignalingClient with a unique clientId.  If the host app
// constructs a second runtime while the first is still alive (HMR / mis-wired
// provider / accidental double-mount), the signaling server sees two distinct
// viewers from the same browser tab and emits spurious `session_conflict`
// events.  Throwing here surfaces the bug at construction time instead of
// causing intermittent eviction loops at runtime.
let activeRuntimeCount = 0;

export function createVgpsLivekitWebRuntime(config: VgpsLivekitWebConfig): VgpsLivekitWebRuntime {
  if (activeRuntimeCount > 0) {
    throw new Error(
      'createVgpsLivekitWebRuntime: another runtime is still active in this page. ' +
      'Call runtime.dispose() before constructing a new one, or share a single instance ' +
      'via your provider (recommended).'
    );
  }
  activeRuntimeCount++;
  try {
    return new RuntimeImpl(config);
  } catch (err) {
    activeRuntimeCount--;
    throw err;
  }
}

function normalizeEvent(e: EventListEvent): RecordingEvent {
  return {
    eventId: e.event_id,
    tsUs: e.ts_us,
    cameraMask: e.camera_mask,
    slotId: e.slot_id,
    type: e.type,
    score: e.score,
    preRollMs: e.pre_roll_ms,
    postRollMs: e.post_roll_ms,
    durationMs: e.duration_ms,
  };
}
